import {
  CircularProgress,
  InputAdornment,
  Select,
  TextField,
} from "@mui/material";
import BreadcrumbPurchase from "./purchaseComponents/BreadcrumbPurchase";
import PurchaseHeader from "./purchaseComponents/PurchaseHeader";
import { Search } from "@mui/icons-material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import TabNavigationTransaction from "./purchaseComponents/TabNavigationTransaction";
import {
  fetchTransactionListPbf,
  getSpPrintPbf,
  postReorderTransaction,
} from "../../../services/purchaseApi";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import PurchasePagination from "./purchaseComponents/PurchasePagination";
import useDebounce from "../../hooks/useDebounce";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import ErrorPopup from "../../common/particles.jsx/ErrorPopup";
import LoadingBackdrop from "../../common/particles.jsx/LoadingBackdrop";
import TransactionActions from "./purchaseComponents/TransactionActions";

const toIDR = (val) =>
  val?.toLocaleString("id-ID", {
    style: "currency",
    currency: "IDR",
  });

const TransactionList = () => {
  const navigate = useNavigate();
  const { statusId } = useParams();

  const [reqBody, setReqBody] = useState({
    limit: 10,
    offset: 1,
    status: 0,
    search: "",
    order_start: null,
    order_end: null,
    payment_status: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [printInv, setPrintInv] = useState(null);

  const debounce = useDebounce(reqBody.search, 500);

  const getStatusColor = (status) => {
    return status === "Menunggu Pembayaran" ||
      status === "Dikomplain" ||
      status === "Dibatalkan"
      ? "red-500"
      : status === "Menunggu Konfirmasi" ||
          status === "Diproses" ||
          status === "Tiba Di Tujuan"
        ? "blue-500"
        : status === "Selesai"
          ? "green-500"
          : "";
  };

  const {
    mutate: mutateList,
    data: dataList,
    isLoading: isLoadingList,
  } = useMutation({
    mutationFn: fetchTransactionListPbf,
    onError: (err) =>
      setErrorMessage(err.message.id ? err.message.id : err.message),
  });

  const refetchList = () =>
    mutateList({
      limit: reqBody.limit,
      offset: reqBody.offset,
      search: debounce,
      status: reqBody.status,
      order_start: reqBody.order_start
        ? moment(reqBody.order_start).format("YYYY-MM-DD")
        : "",
      order_end: reqBody.order_end
        ? moment(reqBody.order_end).format("YYYY-MM-DD")
        : "",
    });

  const { mutate: mutateReorder, isLoading: isLoadingReorder } = useMutation({
    mutationFn: postReorderTransaction,
    onSuccess: () => {
      navigate("/purchase-pbf/cart");
    },
    onError: (err) => {
      setErrorMessage("Tidak dapat menyelesaikan pesanan");
    },
  });

  const { mutate: mutateSP, isLoading: isLoadingSP } = useMutation({
    mutationFn: getSpPrintPbf,
    onSuccess: (data) => {
      if (!data) return;

      const pdfBlob = data; // your PDF Blob
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const title = `SP OLIN ${printInv}`;

      const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        <meta charset="utf-8" />
        <style>
          html, body {
            margin: 0;
            height: 100vh;
            width: 100vw;
            overflow: hidden;
          }
          embed {
            width: 100%;
            height: 100%;
          }
        </style>
      </head>
      <body>
        <embed src="${pdfUrl}" type="application/pdf" />
      </body>
      </html>
      `;

      const htmlBlob = new Blob([html], { type: "text/html" });
      const htmlUrl = URL.createObjectURL(htmlBlob);

      window.open(htmlUrl, "_blank");

      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
        URL.revokeObjectURL(htmlUrl);
        setPrintInv(null);
      }, 3000);
    },
    onError: (err) =>
      setErrorMessage(err.message?.id ? err.message.id : err.message),
  });

  useEffect(() => {
    if (statusId && Number(statusId) !== reqBody.status)
      setReqBody((prevVal) => ({ ...prevVal, status: Number(statusId) }));
  }, [statusId]);

  useEffect(() => {
    mutateList({
      limit: reqBody.limit,
      offset: reqBody.offset,
      search: debounce,
      status: reqBody.status,
      order_start: reqBody.order_start
        ? moment(reqBody.order_start).format("YYYY-MM-DD")
        : "",
      order_end: reqBody.order_end
        ? moment(reqBody.order_end).format("YYYY-MM-DD")
        : "",
      payment_status: "",
    });
  }, [
    reqBody.limit,
    reqBody.offset,
    reqBody.status,
    reqBody.order_start,
    reqBody.order_end,
    debounce,
  ]);

  return (
    <div className="text-[12px] text-[#2C3E5D]">
      <LoadingBackdrop
        isLoading={
          isLoadingReorder || isLoadingSP
        }
      />
      <PurchaseHeader />
      <div className="mt-[70px]">
        <div className="px-3 pt-2 mb-4">
          <BreadcrumbPurchase />
        </div>
        <div className="px-3 mb-4">
          <p className="text-[24px] font-semibold">Daftar Pesanan</p>
        </div>
        <div className="px-3">
          <div className="w-full bg-white rounded-lg p-4 h-fit">
            <p className="text-[16px] mb-2">Filter</p>
            <div className="flex flex-col md:flex-row items-center gap-2 mb-2">
              <TextField
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                placeholder="Cari order id / invoice number / po number"
                value={reqBody.search}
                onChange={(e) =>
                  setReqBody((prevVal) => ({
                    ...prevVal,
                    search: e.target.value,
                  }))
                }
              />

              <div className="w-full md:w-1/2">
                <DesktopDatePicker
                  onChange={(newDate) =>
                    setReqBody((prevVal) => ({
                      ...prevVal,
                      order_start: newDate,
                    }))
                  }
                  value={reqBody.order_start}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        sx={{ width: "100%" }}
                        placeholder="Awal tanggal pesanan"
                        label="Awal tanggal pesanan"
                      />
                    );
                  }}
                />
              </div>
              <div className="w-full md:w-1/2">
                <DesktopDatePicker
                  onChange={(newDate) =>
                    setReqBody((prevVal) => ({
                      ...prevVal,
                      order_end: newDate,
                    }))
                  }
                  value={reqBody.order_end}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        sx={{ width: "100%" }}
                        placeholder="Akhir tanggal pesanan"
                        label="Akhir tanggal pesanan"
                      />
                    );
                  }}
                />
              </div>
            </div>
            <TabNavigationTransaction />

            {dataList &&
            !isLoadingList &&
            dataList.invoices &&
            dataList.total_data > 0 ? (
              dataList.invoices.map((item, i) => {
                return (
                  <div className="rounded-lg shadow-md border p-2 mb-3">
                    <div className="flex justify-between">
                      <p>{moment(item.order_date).format("DD MMMM YYYY")}</p>
                      <div
                        className={`text-${getStatusColor(
                          item.status_label,
                        )} flex items-center gap-2 font-bold`}
                      >
                        <div
                          className={`rounded-full w-[8px] h-[8px] bg-${getStatusColor(
                            item.status_label,
                          )}`}
                        />
                        <p>{item.status_label}</p>
                      </div>
                    </div>
                    <hr className="my-2" />
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 items-center">
                      {/* <div className="w-full">
                        <p>Order ID</p>
                        <p
                          className="text-[14px] cursor-pointer hover:text-gray-300"
                          onClick={() => setOpenDetail(item.invoice_uid)}
                        >
                          {item.nomor_order}
                        </p>
                      </div>
                      <div className="w-full">
                        <p>Nomor PO</p>
                        <p
                          className={`text-[14px] ${item.status !== 10 ? "cursor-pointer  hover:text-gray-300" : ""}`}
                          onClick={() => {
                            if (item.status !== 10) {
                              setPrintInv(item.invoice_uid);
                              setTimeout(() => {
                                mutateSP({
                                  invNum: item.invoice_uid,
                                }); 
                              }, 500);
                            }
                          }}
                        >
                          {item.nomor_po}
                        </p>
                      </div> */}
                      <div className="w-full">
                        <p>Nomor Invoice</p>
                        <p
                          className="text-[14px] cursor-pointer hover:text-gray-300 text-nowrap truncate"
                          onClick={() =>
                            navigate(
                              `/purchase-pbf/detail-transaction/${item.invoice_uid}?from=${statusId ?? 0}`,
                            )
                          }
                        >
                          {item.invoice_no}
                        </p>
                      </div>
                      <div className="w-full">
                        <p>Pelanggan</p>
                        <p className="text-[14px]">{item.company_name}</p>
                      </div>
                    </div>
                    <hr className="my-2" />
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex gap-4 items-stretch">
                        <img
                          src={item.product?.image}
                          className="w-[80px] h-[80px] object-contain rounded-md border"
                        />
                        <div className="w-full">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="text-[16px] font-semibold mb-2">
                              {item.product?.name}
                            </p>
                            {item.product?.categories?.map((cat) => (
                              <p
                                className={`py-1 px-3 mb-2 rounded-full w-fit truncate
                                  ${
                                    cat === "Alat Kesehatan"
                                      ? "bg-[#F9EBFE] text-[#AA31F6]"
                                      : cat === "Obat Obat Tertentu (OOT)"
                                        ? "bg-[#FFE7E7] text-[#C62828]"
                                        : cat === "Obat Prekursor"
                                          ? "bg-[#FFE4E4] text-[#FF0000]"
                                          : cat === "Obat Psikotropika"
                                            ? "bg-[#FEEBF7] text-[#F6317D]"
                                            : "bg-[#EBF5FE] text-[#3194F6]"
                                  }`}
                              >
                                {cat}
                              </p>
                            ))}
                          </div>
                          {/* <p className="mb-2">{item.pbf}</p> */}
                          <p>
                            {toIDR(item.product?.unit_price ?? 0)} x{" "}
                            {item.product?.qty ?? 0}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p>Total Pesanan</p>
                        <p className="text-[16px] font-semibold">
                          {toIDR(item.grand_total)}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <p>
                        {item.other_product_count &&
                        item.other_product_count > 0
                          ? "+" + item.other_product_count + " Lainnya"
                          : ""}
                      </p>
                      <div className="flex gap-2 items-center">
                        <TransactionActions
                          invoice={item}
                          onChanged={refetchList}
                          onError={setErrorMessage}
                        />
                        {item.status_label === "Menunggu Pembayaran" ? (
                          <button
                            type="button"
                            className="text-white bg-[#94C679] hover:bg-gray-300 rounded-md w-fit py-2.5 px-4 text-[14px] disabled:bg-gray-300"
                            onClick={() =>
                              window.open(
                                `${window.location.origin}/purchase-pbf/confirm-checkout/${item.invoice_uid}`,
                                "_blank",
                              )
                            }
                          >
                            Bayar Sekarang
                          </button>
                        ) : item.status_label === "Selesai" ? (
                          <button
                            type="button"
                            className="text-white bg-[#94C679] hover:bg-gray-300 rounded-md w-fit py-2.5 px-4 text-[14px] disabled:bg-gray-300"
                            onClick={() =>
                              mutateReorder({
                                invoice: item.nomor_invoice,
                              })
                            }
                          >
                            Beli Lagi
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex justify-center items-center">
                {isLoadingList ? (
                  <CircularProgress size={20} />
                ) : (
                  <p>Tidak ada data transaksi</p>
                )}
              </div>
            )}

            {dataList && !isLoadingList && dataList.total_data > 0 ? (
              <div className="px-4 mb-4">
                <PurchasePagination
                  offset={reqBody.offset}
                  limit={reqBody.limit}
                  setOffset={(e) =>
                    setReqBody((prevVal) => ({ ...prevVal, offset: e }))
                  }
                  setLimit={(e) => {
                    setReqBody((prevVal) => ({ ...prevVal, limit: e }));
                  }}
                  total={dataList.total_data}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <ErrorPopup
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
    </div>
  );
};

export default TransactionList;
